import { useState } from 'react';
import Input from './ui/Input';
import Button from './ui/Button';
import Icon from './AppIcon';
import { contactService } from '../services/contactService';

interface NewsletterSignupProps {
  variant?: 'default' | 'footer';
}

const NewsletterSignup = ({ variant = 'default' }: NewsletterSignupProps) => {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      await contactService.subscribeToNewsletter({
        email: email.trim(),
        fullName: fullName.trim() || undefined
      });

      setStatus('success');
      setMessage('Successfully subscribed! Check your email for a welcome message.');
      
      // Reset form
      setEmail('');
      setFullName('');

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);

    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || 'Failed to subscribe. Please try again.');
      console.error('Newsletter subscription error:', error);

      // Auto-clear error message after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  if (variant === 'footer') {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-text-primary">
          Subscribe to Our Newsletter
        </h3>
        <p className="text-sm text-text-secondary">
          Get weekly automation tips, case studies, and exclusive offers.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="email"
            name="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === 'loading' || status === 'success'}
          />
          
          <Button
            type="submit"
            variant="default"
            size="default"
            fullWidth
            loading={status === 'loading'}
            disabled={status === 'loading' || status === 'success'}
            iconName="Send"
            iconPosition="right"
          >
            {status === 'success' ? 'Subscribed!' : 'Subscribe'}
          </Button>
        </form>

        {message && (
          <div className={`flex items-start space-x-2 text-sm ${
            status === 'success' ? 'text-success' : 'text-error'
          }`}>
            <Icon 
              name={status === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
              className="flex-shrink-0 mt-0.5" 
            />
            <p>{message}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-brand-primary mb-3">
              Stay Updated with Automation Insights
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Join businesses receiving weekly automation tips, case studies, and exclusive offers. 
              No spam, unsubscribe anytime.
            </p>
          </div>

          {status === 'success' && (
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-success font-medium">Successfully subscribed!</p>
                <p className="text-success text-sm mt-1">Check your email for a welcome message.</p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-error">{message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name (Optional)"
                type="text"
                name="fullName"
                autoComplete="name"
                placeholder="Your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={status === 'loading' || status === 'success'}
              />

              <Input
                label="Email Address"
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === 'loading' || status === 'success'}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={status === 'loading'}
                disabled={status === 'loading' || status === 'success'}
                iconName="Mail"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                {status === 'success' ? 'Subscribed!' : 'Subscribe to Newsletter'}
              </Button>

              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Lock" size={14} />
                  <span>Secure</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Shield" size={14} />
                  <span>Privacy Protected</span>
                </div>
              </div>
            </div>

            <p className="text-xs text-text-secondary text-center">
              By subscribing, you agree to receive marketing emails from Make Mind Automations. 
              You can unsubscribe at any time.
            </p>
          </form>

          <div className="mt-8 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="flex items-center justify-center mb-2">
                  <Icon name="TrendingUp" size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">Weekly Tips</h4>
                <p className="text-sm text-text-secondary">
                  Actionable automation strategies
                </p>
              </div>
              <div>
                <div className="flex items-center justify-center mb-2">
                  <Icon name="BookOpen" size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">Case Studies</h4>
                <p className="text-sm text-text-secondary">
                  Real success stories and results
                </p>
              </div>
              <div>
                <div className="flex items-center justify-center mb-2">
                  <Icon name="Gift" size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary mb-1">Exclusive Offers</h4>
                <p className="text-sm text-text-secondary">
                  Early access and special discounts
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;